import { ReviewOffer } from "../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { getMonthProperty } from "utils/usageUtils";

type SelfSupplyChartSummary = {
  selfSupplyKwh: number;
  utilityImportKwh: number;
  homeEnergyUsageKwh: number;
  solarProductionKwh: number;
  utilityExportKwh: number;
};

type SelfSupplyMonthlySeries = {
  usage: number[];
  production: number[];
  selfSupply: number[];
  utilityImport: number[];
};

const getMonthlySelfSupplyPercent = (monthly?: number[]): number[] => {
  if (!monthly?.length) {
    return Array(12).fill(0);
  }

  return monthly.slice(0, 12).map((value) => value ?? 0);
};

const getMonthlySelfSupplyKwh = (
  usage: number[],
  monthlyPercent: number[]
): number[] =>
  usage.map((value, index) =>
    Math.round((value * monthlyPercent[index]) / 100)
  );

const hasMonthlySelfSupplyData = (offer: ReviewOffer | undefined): boolean => {
  const monthlyPercent = getMonthlySelfSupplyPercent(
    offer?.monthlySelfSupplyPercent
  );
  return monthlyPercent.some((value) => value > 0);
};

const hasAnnualSelfSupplyData = (offer: ReviewOffer | undefined): boolean =>
  offer?.annualSelfSupplyPercent !== undefined &&
  offer?.annualSelfSupplyPercent !== null;

const getComparisonSelfSupplyPercentFromValue = (
  annualPercent: number | string | undefined | null
): number => {
  if (annualPercent === undefined || annualPercent === null) {
    return 0;
  }

  const numeric = Number(annualPercent);
  if (!Number.isFinite(numeric)) {
    return 0;
  }

  return Math.min(100, Math.max(0, Math.floor(numeric)));
};

const getOfferReviewComparisonSelfSupplyPercent = (
  offer: ReviewOffer
): number =>
  getComparisonSelfSupplyPercentFromValue(offer.annualSelfSupplyPercent);

const getSelfSupplyMonthlySeries = (
  offer: ReviewOffer
): SelfSupplyMonthlySeries | undefined => {
  if (!hasMonthlySelfSupplyData(offer)) {
    return undefined;
  }

  const usage = getMonthProperty(offer.usage);
  const production = getMonthProperty(offer.monthlyProduction);
  const monthlyPercent = getMonthlySelfSupplyPercent(
    offer.monthlySelfSupplyPercent
  );
  const selfSupply = getMonthlySelfSupplyKwh(usage, monthlyPercent);

  return {
    usage,
    production,
    selfSupply,
    utilityImport: usage.map((value, index) =>
      Math.max(0, value - selfSupply[index])
    ),
  };
};

const getSelfSupplyChartSummary = (
  offer: ReviewOffer
): SelfSupplyChartSummary | undefined => {
  const monthlySeries = getSelfSupplyMonthlySeries(offer);
  if (!monthlySeries || !offer.consumption) {
    return undefined;
  }

  const selfSupplyKwh = Math.round(
    monthlySeries.selfSupply.reduce((sum, value) => sum + value, 0)
  );
  const homeEnergyUsageKwh = Math.round(offer.consumption);
  const solarProductionKwh = Math.round(offer.production ?? 0);

  return {
    selfSupplyKwh,
    utilityImportKwh: Math.max(0, homeEnergyUsageKwh - selfSupplyKwh),
    homeEnergyUsageKwh,
    solarProductionKwh,
    utilityExportKwh: Math.max(0, solarProductionKwh - selfSupplyKwh),
  };
};

export type { SelfSupplyChartSummary, SelfSupplyMonthlySeries };
export {
  getComparisonSelfSupplyPercentFromValue,
  getMonthlySelfSupplyKwh,
  getMonthlySelfSupplyPercent,
  getOfferReviewComparisonSelfSupplyPercent,
  getSelfSupplyChartSummary,
  getSelfSupplyMonthlySeries,
  hasMonthlySelfSupplyData,
  hasAnnualSelfSupplyData,
};
